import type { AiCapabilityPage } from "../schema.js";
import { LIVE, MANAGER_ROLES, SLOT_FUND, WRITE_FLAGS } from "./_shared.js";

/**
 * `EXP-F-EQUAL` (@UC0203) — ghi chi CHIA ĐỀU cho thành viên, T2. QĐ PO 15/09: GIỮ.
 *
 * Executor = **`ExpenseService.create`** (id riêng `expenseEqual.create`), CÙNG hàm với `EXP-F-NOALLOC`
 * (§3.8), khác đúng ở `splitRule: "equal"` + `participants`.
 * ⚠️ Câu cũ ở đây ghi *"Executor = `EmergencyExpenseService.create`"* — **SAI**, và đó là cửa mà bản
 * `draft` từng khai: `CreateEmergencyExpenseInput` không có `splitRule` lẫn `participants` nên nó KHÔNG
 * chia đều được (đính chính 0042, cùng lỗi mà 0041 đã sửa cho trang kia).
 *
 * Chia qua `splitEngine`: Σ suất = tổng, đồng lẻ có tên người gánh (§3.5) — phiếu xác nhận PHẢI nêu
 * `{ganhLe}` — MỘT chỗ chèn mang cả cụm câu: dư ∈ [0, N−1] nên người gánh lẻ có thể trả **trọn cả
 * khoản chi** (10 người chia 5đ ⇒ 9 người 0đ), mà nhóm **chia chẵn** thì không được thấy một vế nào
 * về đồng lẻ. Nêu tên mà không nêu số chỉ thoả một nửa; in vế đó khi không có dư là nói sai (rà #905).
 */
export const EXP_F_EQUAL = {
  id: "EXP-F-EQUAL",
  uc: "@UC0203",
  tier: "T2",
  status: LIVE,
  roles: MANAGER_ROLES,
  flags: WRITE_FLAGS,
  slots: [
    SLOT_FUND,
    { id: "soTien", type: "money", required: true, ask: "Tổng chi bao nhiêu?" },
    { id: "noiDung", type: "text", required: true, ask: "Chi cho việc gì?" },
    // 🔴 0109 S13 (@UC0248, QĐ PO 5b): người ứng = người lớn có vai quản lý + suất đã có tài khoản — KHÔNG phạm vi nghĩa vụ
    // (E2E 25/09: chip mời suất chưa ai nhận ⇒ `PAYER_NOT_IN_GROUP`). Binding kiểm bằng CÙNG luật.
    { id: "nguoiUng", type: "member", required: true, ask: "Ai ứng tiền trả trước?", memberScope: "payer" },
    {
      id: "nguoi",
      type: "list",
      required: false,
      ask: "Chia cho ai? (bỏ trống = tất cả thành viên)",
      clarify: "Chia đều cho tất cả, hay chỉ một số người?",
      // 🔴 US-AIV2-0033 vòng 3: MỌI ô `list` phải có câu hỏi cho ca **span cắt bớt** (`listSpanCut`), không chỉ ô khai
      // `duplicates: "ask"`. Thiếu nó thì span cắt ⇒ hỏi lại bằng câu `ask` chung ⇒ người dùng gửi y nguyên ⇒ vòng lặp.
      // (Viết khi trang còn `draft`; nay trang đã `bound` nên vế này đang thật sự gác — 0042.)
      partialAsk: "Mình mới đọc được một phần danh sách, sợ chia thiếu người nên chưa dám ghi. Bạn gửi lại danh sách giúp mình nhé.",
    },
  ],
  llm: {
    description: "Ghi một khoản chi và chia đều gánh nặng cho các thành viên (mỗi người một phần bằng nhau).",
    examples: [
      "ghi chi 600k tiền ăn, chia đều cả nhóm",
      "ghi chi 600k tien an chia deu",
      "chi 1 triệu liên hoan, mọi người chia đều",
      "chi 1 trieu lien hoan moi nguoi chia deu",
      "Minh ứng 900k mua áo đội, chia đều 9 người",
      "minh ung 900k chia deu 9 nguoi",
      "chia đều 500k",
      "ghi chi chia đều",
      "tiền xe 1tr2, cả nhóm cưa đôi… à chia đều nhé",
      "chi 800k chia đều cho các phụ huynh",
      "chia đều tiền ăn",
      "split đều 300k",
      // ——— 0105: thêm cho đủ 40 ———
      "ghi khoản chi 450k mua nước, chia đều mọi người",
      "ghi khoan chi 450k mua nuoc chia deu moi nguoi",
      "mua quà 2 triệu, chia đều các phụ huynh",
      "mua qua 2 trieu chia deu cac phu huynh",
      "An ứng 1tr5 thuê sân, chia đều cho cả đội",
      "an ung 1tr5 thue san chia deu ca doi",
      "chi phí in tài liệu 360k chia đều nhau",
      "chi phi in tai lieu 360k chia deu nhau",
      "tiền thuê xe 3 triệu chia đều cho từng người",
      "tien thue xe 3 trieu chia deu cho tung nguoi",
      "khoản chi này chia đều được không",
      "khoan chi nay chia deu duoc khong",
      "ghi chi 750k, mỗi người gánh một phần bằng nhau",
      "ghi chi 750k moi nguoi ganh mot phan bang nhau",
      "Bình trả trước 480k tiền bánh, chia đều nhé",
      "binh tra truoc 480k tien banh chia deu nhe",
      "ghi chi trang trí lớp 900k chia đều 30 bạn",
      "ghi chi trang tri lop 900k chia deu 30 ban",
      "chia đều hoá đơn 1tr cho nhóm",
      "chia deu hoa don 1tr cho nhom",
      "ghi chi và chia đầu người",
      "ghi chi va chia dau nguoi",
      "mua đồ hết 620k, cưa đều ra",
      "mua do het 620k cua deu ra",
      "chi 250k chia đều cho 5 người",
      "chi 250k chia deu cho 5 nguoi",
      "ghi khoản chi rồi chia đều cho các thành viên",
      "ghi khoan chi roi chia deu cho cac thanh vien",
    ],
    // AI-INTERP M12 (QĐ I12): câu THỬ — KHÔNG vào prompt/BM25; viết đời thường, không chép ví dụ (validator HELD_OUT_IS_EXAMPLE).
    heldOutExamples: [
      "an lau het 1tr8, chia deu moi nguoi nha",
      "tiền xe đi biển 4 triệu, cả nhóm cưa đều ạ",
      "chi Hai ung 600k mua banh, chia deu ca lop",
      "bữa nhậu 900k chia đều anh em nhé",
      "tien san 400k chia deu 8 dua",
      "ghi giup em khoan chi 300k, moi nha gop bang nhau",
    ],
    // 🔴 US-AIV2-0111 C06 (@UC0320) — câu HỎI (việc XEM) không thuộc trang ghi này; mỗi câu là ví dụ của một trang đọc ⇒ eval đo được.
    questionsNotHere: ["mình đang nợ bạn nào bao nhiêu"],
  },
  /**
   * 🔴 ĐÍNH CHÍNH 22/09 (US-AIV2-0042): bản `draft` khai `emergencyExpense.create` — **SAI**.
   * `CreateEmergencyExpenseInput` (`emergency-expense.service.ts:63`) **không có** `splitRule` lẫn
   * `participants` ⇒ nó không chia đều được. Cửa đúng là **CÙNG hàm với `EXP-F-NOALLOC`**
   * (`ExpenseService.create`, `money-engine/.../expense.service.ts:91,113,122`) — 0023 truyền
   * `splitRule: "equal"`. Một trang chia đều và một trang không chia **không được** đi hai cửa khác nhau.
   */
  /**
   * 🔴 US-AIV2-0110 S03 (@UC0260 · QĐ PO 4) — ĐÁP ÁN TIỀN của bộ eval, VIẾT TAY từ chính câu (không từ đầu ra model).
   * KHÔNG vào prompt ⇒ không đổi hash theo ca. Câu vắng mặt ở đây: không nói số tiền.
   * Không khai: "1tr2" (#09) · "1tr5" (#17/#18) — đuôi số ghép, sản phẩm HỎI LẠI (money-parser luật 4).
   */
  evalAmounts: {
    "ghi chi 600k tiền ăn, chia đều cả nhóm": "600000",
    "ghi chi 600k tien an chia deu": "600000",
    "chi 1 triệu liên hoan, mọi người chia đều": "1000000",
    "chi 1 trieu lien hoan moi nguoi chia deu": "1000000",
    "Minh ứng 900k mua áo đội, chia đều 9 người": "900000",
    "minh ung 900k chia deu 9 nguoi": "900000",
    "chia đều 500k": "500000",
    "chi 800k chia đều cho các phụ huynh": "800000",
    "split đều 300k": "300000",
    "ghi khoản chi 450k mua nước, chia đều mọi người": "450000",
    "ghi khoan chi 450k mua nuoc chia deu moi nguoi": "450000",
    "mua quà 2 triệu, chia đều các phụ huynh": "2000000",
    "mua qua 2 trieu chia deu cac phu huynh": "2000000",
    "chi phí in tài liệu 360k chia đều nhau": "360000",
    "chi phi in tai lieu 360k chia deu nhau": "360000",
    "tiền thuê xe 3 triệu chia đều cho từng người": "3000000",
    "tien thue xe 3 trieu chia deu cho tung nguoi": "3000000",
    "ghi chi 750k, mỗi người gánh một phần bằng nhau": "750000",
    "ghi chi 750k moi nguoi ganh mot phan bang nhau": "750000",
    "Bình trả trước 480k tiền bánh, chia đều nhé": "480000",
    "binh tra truoc 480k tien banh chia deu nhe": "480000",
    "ghi chi trang trí lớp 900k chia đều 30 bạn": "900000",
    "ghi chi trang tri lop 900k chia deu 30 ban": "900000",
    "chia đều hoá đơn 1tr cho nhóm": "1000000",
    "chia deu hoa don 1tr cho nhom": "1000000",
    "mua đồ hết 620k, cưa đều ra": "620000",
    "mua do het 620k cua deu ra": "620000",
    "chi 250k chia đều cho 5 người": "250000",
    "chi 250k chia deu cho 5 nguoi": "250000",
  },
  executor: "expenseEqual.create",
  /** AI-INTERP M13 (@UC0173): ca eval GIỮA FORM (trang này đang mở) — Sổ là nguồn; không vào prompt/BM25. Bộ thu dựng prompt như cổng. */
  evalInForm: [
    { askedSlot: "nguoiUng", filledSlots: ["soTien", "noiDung"], history: [{ role: "user", content: "chi 600k tiền ăn chia đều" }, { role: "assistant", content: "Ai ứng tiền trả trước?" }], input: "Lan ứng", expect: { kind: "inFormAnswer", slot: "nguoiUng" } },
    { askedSlot: "soTien", filledSlots: ["noiDung"], input: "stk nhóm là gì", expect: { kind: "page", page: "GROUP-BANK-ACCOUNT" } },
  ],
  /** `@UC0216` — cửa mobile trang này chịu trách nhiệm: cùng cửa `POST /funds/{fundId}/expenses` với `EXP-F-NOALLOC`. */
  endpoints: [],
  facts: ["tenQuy", "ngayGhi", "tenNguoiUng", "soNguoi", "moiNguoi", "ganhLe", "ketQua"],
  templates: {
    confirm: "Ghi chi {soTien} “{noiDung}” vào quỹ {tenQuy}, ngày {ngayGhi}, chia đều {soNguoi} người, mỗi người {moiNguoi} — {ganhLe}; người ứng: {tenNguoiUng}?",
    done: "Đã ghi chi {soTien} “{noiDung}” ngày {ngayGhi}, chia đều {soNguoi} người × {moiNguoi} — {ganhLe}. {tenNguoiUng} được ghi là người ứng. {ketQua}",
  },
  labels: {
    soTien: "Tổng chi",
    noiDung: "Nội dung",
    tenQuy: "Quỹ",
    // review #1412 HIGH-1 (AI-INV-4): sổ ghi ngày chi = NGÀY PHIẾU ⇒ thẻ in đúng ngày đó (cùng nhãn "Ngày chi" với EXP-F-NOALLOC).
    ngayGhi: "Ngày chi",
    soNguoi: "Số người chia",
    moiNguoi: "Mỗi người",
    /*
     * 🔴 §3.5 — "đồng lẻ có tên người gánh". MỘT chỗ chèn mang cả cụm câu, không phải hai.
     *
     * Bản trước có `{nguoiGanhLe}` + `{moiNguoiGanhLe}` riêng, và `templates.confirm` là **chuỗi cố
     * định không nhánh** ⇒ ca **chia chẵn** vẫn in *"riêng không ai (chia chẵn) trả 100.000đ (gánh đồng
     * lẻ)"*, cộng một dòng thẻ T2 khiến người đọc tưởng có **phụ thu**. Một chỗ chèn cho phép cụm câu
     * đổi hẳn theo trạng thái dư (`ganhLeText`), nên nhóm chia chẵn không thấy một vế nào về đồng lẻ.
     */
    ganhLe: "Đồng lẻ",
    /*
     * 🔴 CRITICAL-2 (rà #905 vòng 3) — `create` trả BA trạng thái (`posted` · `cooling` ·
     * `pending_decision`), và nhánh nào chạy là do **cấu hình nhóm**. Câu `done` trước đây khai
     * "Đã ghi chi …" VÔ ĐIỀU KIỆN ⇒ với nhóm bật duyệt, trợ lý báo "đã ghi" về một khoản **0 bút
     * toán, đang chờ người duyệt**. Chỉ có ở `done` (lúc `confirm` thì chưa ghi nên chưa biết).
     */
    ketQua: "Kết quả",
    tenNguoiUng: "Người ứng tiền",
  },
  journey: { outcomes: { done: { next: ["READ-FUND-MONEY", "READ-PEOPLE-MINE"] } } },
  help: { title: "Ghi chi chia đều", tryNow: "ghi chi 600k tiền ăn, chia đều cả nhóm" },
} satisfies AiCapabilityPage;
